/** Centralized Axios instance with request/response interceptors. */

import axios, { type AxiosRequestConfig } from 'axios';

import { API_BASE_URL_OVERRIDE, IS_DEV } from '@/config/env';

// API base URL from environment or default to backend
const API_BASE_URL: string = API_BASE_URL_OVERRIDE ?? 'http://localhost:5000/api';

// Create Axios instance with default configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds
  withCredentials: true, // Include cookies for session-based auth
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Normalized API error. Replaces the previous plain `{ status, message, data }`
 * object literal rejection so callers can `instanceof`-check it and `data`
 * stays `unknown` until narrowed rather than silently being `any`.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly data: unknown;

  constructor(status: number, message: string, data: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError;

const extractErrorMessage = (data: unknown, fallback: string): string => {
  if (typeof data === 'object' && data !== null && 'message' in data) {
    const { message } = data;
    if (typeof message === 'string') {
      return message;
    }
  }
  return fallback;
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    if (IS_DEV) {
      // eslint-disable-next-line no-console -- intentional dev-only request logging
      console.log(
        `[API Request] ${config.method?.toUpperCase() ?? ''} ${config.url ?? ''}`,
        config.data
      );
    }

    return config;
  },
  (error: unknown) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error instanceof Error ? error : new Error(String(error)));
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Log response in development
    if (IS_DEV) {
      // eslint-disable-next-line no-console -- intentional dev-only response logging
      console.log(
        `[API Response] ${response.config.method?.toUpperCase() ?? ''} ${response.config.url ?? ''}`,
        response.data
      );
    }

    return response;
  },
  (error: unknown) => {
    // Handle errors globally
    console.error('[API Response Error]', error);

    if (axios.isAxiosError<unknown>(error)) {
      if (error.response) {
        // Server responded with error status
        const { status, data } = error.response;

        switch (status) {
          case 401:
            // Don't intercept auth check endpoints — let components handle auth errors
            if (
              !error.config?.url?.includes('/auth/login') &&
              !error.config?.url?.includes('/auth/me')
            ) {
              console.warn('Unauthorized access - redirecting to login');
              window.dispatchEvent(new CustomEvent('auth:unauthorized'));
            }
            break;

          case 403:
            // Forbidden - insufficient permissions
            console.warn('Forbidden - insufficient permissions');
            break;

          case 404:
            // Not found
            console.warn('Resource not found');
            break;

          case 500:
            // Internal server error
            console.error('Internal server error');
            break;

          default:
            console.error(`HTTP ${status} error`);
        }

        // Return formatted error
        return Promise.reject(new ApiError(status, extractErrorMessage(data, error.message), data));
      } else if (error.request) {
        // Request made but no response received
        console.error('No response received from server');
        return Promise.reject(
          new ApiError(0, 'Network error - please check your connection', null)
        );
      }

      // Request setup error
      console.error('Request setup error:', error.message);
      return Promise.reject(new ApiError(0, error.message || 'Request failed', null));
    }

    const message = error instanceof Error ? error.message : 'Request failed';
    return Promise.reject(new ApiError(0, message, null));
  }
);

// API service methods

/**
 * GET request
 */
export const get = <T>(url: string, config: AxiosRequestConfig = {}): Promise<T> => {
  return api.get<T>(url, config).then((response) => response.data);
};

/**
 * POST request
 */
export const post = <T>(
  url: string,
  data: unknown = {},
  config: AxiosRequestConfig = {}
): Promise<T> => {
  return api.post<T>(url, data, config).then((response) => response.data);
};

/**
 * PUT request
 */
export const put = <T>(
  url: string,
  data: unknown = {},
  config: AxiosRequestConfig = {}
): Promise<T> => {
  return api.put<T>(url, data, config).then((response) => response.data);
};

/**
 * PATCH request
 */
export const patch = <T>(
  url: string,
  data: unknown = {},
  config: AxiosRequestConfig = {}
): Promise<T> => {
  return api.patch<T>(url, data, config).then((response) => response.data);
};

/**
 * DELETE request
 */
export const del = <T>(url: string, config: AxiosRequestConfig = {}): Promise<T> => {
  return api.delete<T>(url, config).then((response) => response.data);
};

// Export default API instance and methods
export default api;
export { API_BASE_URL };
